import React, { useMemo, useState } from 'react';
import { ToggleButton, ToggleButtonGroup, Stack } from '@mui/material';
import { MEASURES, REVENUE_PARTS, DEFAULT_PARTS, metricAccessor, isMoneyMeasure } from '../lib/periods';

/**
 * Measure + revenue-parts selection shared by every KPI report.
 * Exposes { measure, parts, get, money } and the toggle setters; `get` is the
 * accessor to feed the aggregation helpers / crossTabByYear. `parts` is which
 * revenue components are included (Net and/or Postage); at least one stays on.
 */
export function useMeasure(initialMeasure = 'revenue', initialParts = DEFAULT_PARTS) {
  const [measure, setMeasure] = useState(initialMeasure);
  const [parts, setParts] = useState(initialParts);
  const get = useMemo(() => metricAccessor(measure, parts), [measure, parts]);
  return { measure, setMeasure, parts, setParts, get, money: isMoneyMeasure(measure) };
}

/** The measure toggle plus Net / Postage part toggles (shown only for Revenue). */
export function MeasureControls({ measure, setMeasure, parts, setParts }) {
  return (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
      <ToggleButtonGroup size="small" exclusive value={measure} onChange={(_, v) => v && setMeasure(v)}>
        {Object.entries(MEASURES).map(([k, m]) => (
          <ToggleButton key={k} value={k}>{m.label}</ToggleButton>
        ))}
      </ToggleButtonGroup>
      {measure === 'revenue' && (
        <ToggleButtonGroup
          size="small"
          value={parts}
          onChange={(_, v) => v.length && setParts(v)} // keep at least one selected
        >
          {Object.entries(REVENUE_PARTS).map(([k, p]) => (
            <ToggleButton key={k} value={k}>{p.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
    </Stack>
  );
}
